import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "@/components/ui/sonner";
import { ReferralPanel } from "@/components/ReferralPanel";
import { TitleOverridePanel } from "@/components/admin/TitleOverridePanel";
import { BrokenLinksPanel } from "@/components/admin/BrokenLinksPanel";
import { DuplicatesPanel } from "@/components/admin/DuplicatesPanel";
import { AdminSidebar, AdminViewHeader, Fold } from "@/components/admin/AdminShell";
import { DashboardStats, ImportHistory } from "@/components/admin/DashboardStats";
import { PlaylistImportPanel } from "@/components/admin/PlaylistImportPanel";
import { ListPlus } from "@phosphor-icons/react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  FilmSlate,
  YoutubeLogo,
  Archive,
  UploadSimple,
  Key,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  MusicNotes,
  Lightning,
  Baby,
  Broom,
  ClockCounterClockwise,
  Pulse,
  ArrowClockwise,
  Prohibit,
  LinkSimple,
  CurrencyDollar,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "reelvault_admin_token";

const Status = ({ state }) => {
  if (!state || !state.type) return null;
  const ok = state.type === "ok";
  return (
    <div
      data-testid="import-status"
      className={`mt-4 flex items-center gap-2 border px-4 py-3 font-mono text-xs ${
        state.type === "info" ? "border-white/20 text-white/70" : ok ? "border-amber/40 text-amber" : "border-crimson/50 text-crimson"
      }`}
    >
      {state.type === "info" ? <CircleNotch size={18} className="animate-spin" /> : ok ? <CheckCircle size={18} weight="duotone" /> : <WarningCircle size={18} weight="duotone" />}
      <span>{state.msg}</span>
    </div>
  );
};

const Panel = ({ icon, title, children }) => (
  <section className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
    <div className="flex items-center gap-3 mb-6">
      {icon}
      <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">{title}</h2>
    </div>
    {children}
  </section>
);

const field =
  "w-full bg-[#111] border border-white/10 px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors font-mono text-sm";
const btn =
  "flex items-center justify-center gap-2 border border-amber bg-amber/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-amber hover:bg-amber hover:text-black transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed";
const label = "block font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-2";

// Visual indicator of which waterfall tier auto-classified a video.
const TIER_META = {
  tmdb: { label: "TMDb", cls: "border-amber/50 text-amber bg-amber/10" },
  channel: { label: "Channel", cls: "border-sky-400/50 text-sky-300 bg-sky-400/10" },
  keyword: { label: "Regex", cls: "border-emerald-400/50 text-emerald-300 bg-emerald-400/10" },
  uncategorized: { label: "Needs Review", cls: "border-crimson/50 text-crimson bg-crimson/10" },
  manual: { label: "Manual", cls: "border-violet-400/50 text-violet-300 bg-violet-400/10" },
};
const TierBadge = ({ source }) => {
  const m = TIER_META[source] || { label: source || "—", cls: "border-white/15 text-white/40" };
  return (
    <span
      data-testid={`tier-badge-${source || "none"}`}
      className={`inline-flex items-center shrink-0 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${m.cls}`}
    >
      {m.label}
    </span>
  );
};

const HEALTH_META = {
  healthy: { label: "Healthy", cls: "border-emerald-400/50 text-emerald-300 bg-emerald-400/10" },
  warning: { label: "Warning", cls: "border-amber/50 text-amber bg-amber/10" },
  offtopic: { label: "Off-Topic", cls: "border-crimson/60 text-crimson bg-crimson/10" },
};
const HealthBadge = ({ status }) => {
  const m = HEALTH_META[status] || { label: status || "—", cls: "border-white/15 text-white/40" };
  return (
    <span
      data-testid={`health-badge-${status || "none"}`}
      className={`inline-flex items-center shrink-0 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${m.cls}`}
    >
      {m.label}
    </span>
  );
};

// Owner session cookie authorizes admin calls; the token stays optional for scripts.
axios.defaults.withCredentials = true;

export default function Admin() {
  const { user, loading } = useAuth() || {};
  if (loading) return <div data-testid="admin-auth-loading" className="min-h-screen bg-[#050505]" />;
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return <AdminConsole />;
}

function AdminConsole() {
  const isOwner = true;
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTabState] = useState(localStorage.getItem("rv_admin_tab") || "dashboard");
  const setTab = (t) => {
    setTabState(t);
    localStorage.setItem("rv_admin_tab", t);
    window.scrollTo({ top: 0 });
  };
  const [ytHandles, setYtHandles] = useState("@shudder");
  const [ytMax, setYtMax] = useState(15);
  const [ytGenre, setYtGenre] = useState("Indie");
  const [ytmTargets, setYtmTargets] = useState(
    "@thezeusnetwork7574 | Reality TV\n@FunClub-n3v | Comedy\n@MovieSphereHorror-SciFi | Horror\n@QuiverDistributionMovies | Action\n@CineFlexEntertainment | Action\n@stash_suspense_thriller | Suspense\n@BlackRidgefilms1 | Action\n@BLKScreenCentral | Romance\nFilmRise Movies full movie | Drama\nMovie Central full movie | Action\nPopcornflix full movie | Comedy\nHorror Central full movie | Horror\nFilmIsNow Horror Movies | Horror\nFree Movie Lab full movie | Crime"
  );
  const [ytmMax, setYtmMax] = useState(8);
  const [musicTargets, setMusicTargets] = useState(
    "official music video VEVO | Music\nindie official music video | Music"
  );
  const [musicMax, setMusicMax] = useState(12);
  const [tmdbQuery, setTmdbQuery] = useState("Bad Boys");
  const [iaCollection, setIaCollection] = useState("prelinger");
  const [iaRows, setIaRows] = useState(30);
  const [csvFile, setCsvFile] = useState(null);
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState({});
  const [syncSummary, setSyncSummary] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [uncatItems, setUncatItems] = useState([]);
  const [uncatTotal, setUncatTotal] = useState(0);
  const [uncatTags, setUncatTags] = useState({});
  const [auditItems, setAuditItems] = useState([]);
  const [auditCounts, setAuditCounts] = useState({});
  const [retagInputs, setRetagInputs] = useState({});
  const [healthChannels, setHealthChannels] = useState([]);
  const [healthSummary, setHealthSummary] = useState({});
  const [healthLoading, setHealthLoading] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTags, setLinkTags] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [syncYt, setSyncYt] = useState(true);
  const [syncTmdb, setSyncTmdb] = useState(true);
  const [syncArchive, setSyncArchive] = useState(true);
  const [syncFreeMovies, setSyncFreeMovies] = useState(true);
  const [syncKids, setSyncKids] = useState(true);
  const [syncMusic, setSyncMusic] = useState(true);
  const [syncDiscovery, setSyncDiscovery] = useState(true);
  const [syncNewSources, setSyncNewSources] = useState(true);
  const [ytMaxSync, setYtMaxSync] = useState(3);

  // Holds the active status-poll interval so it is always cleared (on finish AND on
  // unmount) — prevents a leaked timer firing setState on an unmounted component.
  const pollRef = useRef(null);
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const saveToken = (val) => {
    setToken(val);
    localStorage.setItem(TOKEN_KEY, val);
  };

  const headers = { "X-Admin-Token": token };
  const authed = !!token || isOwner;

  const setKeyStatus = (key, type, msg) => setStatus((s) => ({ ...s, [key]: { type, msg } }));

  const loadSyncLogs = async () => {
    if (!authed) return;
    try {
      const { data } = await axios.get(`${API}/admin/sync-logs?limit=10`, { headers: { "X-Admin-Token": token } });
      setSyncLogs(data.runs || []);
    } catch {
      /* ignore — token may be unset/invalid */
    }
  };

  const loadUncategorized = async () => {
    if (!authed) return;
    try {
      const { data } = await axios.get(`${API}/admin/uncategorized?limit=100`, { headers: { "X-Admin-Token": token } });
      setUncatItems(data.items || []);
      setUncatTotal(data.total || 0);
    } catch {
      /* ignore */
    }
  };

  const loadAudit = async () => {
    if (!authed) return;
    try {
      const { data } = await axios.get(`${API}/admin/recent-tagged?limit=80`, { headers: { "X-Admin-Token": token } });
      setAuditItems(data.items || []);
      setAuditCounts(data.counts || {});
    } catch {
      /* ignore */
    }
  };

  const loadHealth = async () => {
    if (!authed) return;
    setHealthLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/channel-health`, { headers: { "X-Admin-Token": token } });
      setHealthChannels(data.channels || []);
      setHealthSummary(data.summary || {});
    } catch {
      /* ignore */
    } finally {
      setHealthLoading(false);
    }
  };

  const loadRevenue = async () => {
    if (!authed) return;
    setRevenueLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliate-clicks?limit=25`, { headers: { "X-Admin-Token": token } });
      setRevenue(data);
    } catch {
      /* ignore */
    } finally {
      setRevenueLoading(false);
    }
  };

  const addByLink = async () => {
    const url = linkUrl.trim();
    if (!url) {
      toast.error("Paste a YouTube link first");
      return;
    }
    const keywords = linkTags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    setLinkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/import/youtube-link`, { url, keywords }, { headers });
      toast.success("Added to catalog", {
        description: `${data.title} (${Math.round((data.duration_sec || 0) / 60)} min)`,
      });
      setLinkUrl("");
      setLinkTags("");
      loadAudit();
      loadHealth();
    } catch (e) {
      toast.error("Could not add this video", { description: e.response?.data?.detail || "Check the link and token." });
    } finally {
      setLinkBusy(false);
    }
  };

  const quarantineChannel = async (channel, quarantined) => {
    const action = quarantined ? "release" : "quarantine";
    if (!quarantined && !window.confirm(`Quarantine "${channel}"? This instantly hides its ENTIRE catalog from every public row. You can release it anytime.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/quarantine-channel`, { channel, action }, { headers });
      toast.success(action === "quarantine" ? "Channel quarantined" : "Channel released", {
        description: `${data.videos_affected} videos ${action === "quarantine" ? "hidden" : "restored"}`,
      });
      loadHealth();
    } catch (e) {
      toast.error("Action failed", { description: e.response?.data?.detail || "Check token." });
    }
  };

  useEffect(() => {
    loadSyncLogs();
    loadUncategorized();
    loadAudit();
    loadHealth();
    loadRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const retagVideo = async (videoId) => {
    const raw = retagInputs[videoId] || "";
    const tags = raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    if (!tags.length) {
      toast.error("Enter at least one genre tag (comma-separated)");
      return;
    }
    try {
      await axios.post(`${API}/admin/retag`, { video_id: videoId, tags }, { headers });
      toast.success("Re-tagged", { description: tags.join(", ") });
      setRetagInputs((t) => ({ ...t, [videoId]: "" }));
      loadAudit();
      loadUncategorized();
    } catch (e) {
      toast.error("Re-tag failed", { description: e.response?.data?.detail || "Check token." });
    }
  };

  const approveUncat = async (videoId) => {
    const raw = uncatTags[videoId] || "";
    const tags = raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    if (!tags.length) {
      toast.error("Enter at least one genre tag (comma-separated)");
      return;
    }
    try {
      await axios.post(`${API}/admin/uncategorized/approve`, { video_id: videoId, tags }, { headers });
      toast.success("Approved — routed to its genre rows", { description: tags.join(", ") });
      setUncatItems((items) => items.filter((v) => v.video_id !== videoId));
      setUncatTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error("Approve failed", { description: e.response?.data?.detail || "Check token." });
    }
  };

  const rejectUncat = async (videoId) => {
    try {
      await axios.post(`${API}/admin/uncategorized/reject`, { video_id: videoId }, { headers });
      toast.success("Removed from the catalog");
      setUncatItems((items) => items.filter((v) => v.video_id !== videoId));
      setUncatTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error("Reject failed", { description: e.response?.data?.detail || "Check token." });
    }
  };

  const runSyncAll = async () => {
    setBusy("sync");
    setKeyStatus("sync", null, "");
    setSyncSummary(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/sync-all`,
        {
          sync_tmdb: syncTmdb,
          sync_archive: syncArchive,
          sync_youtube: syncYt,
          sync_free_movies: syncFreeMovies,
          sync_kids: syncKids,
          sync_music: syncMusic,
          sync_discovery: syncDiscovery,
          sync_new_sources: syncNewSources,
          yt_max_results: Number(ytMaxSync),
        },
        { headers, timeout: 30000 }
      );
      if (data.status === "already_running") {
        setKeyStatus("sync", "ok", "A sync is already running in the background…");
        toast.info("A sync is already in progress");
      } else {
        setKeyStatus("sync", "ok", "Sync started in the background — watching for results…");
        toast.success("Auto-Sync started", { description: "Running in the background. This can take a few minutes." });
      }
      // Poll status until the background job finishes. Each poll has a short timeout and
      // swallows gateway errors (502/504) so a slow request can never crash the app.
      // Bounded to ~15 min so a stuck job can't leave the button spinning forever.
      await new Promise((resolve) => {
        const startedAt = Date.now();
        const MAX_POLL_MS = 15 * 60 * 1000;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          if (Date.now() - startedAt > MAX_POLL_MS) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setKeyStatus("sync", "ok", "Still syncing on the server — check the Recent Sync Runs log shortly.");
            resolve();
            return;
          }
          try {
            const { data: s } = await axios.get(`${API}/admin/sync-status`, { headers, timeout: 15000 });
            if (!s.running) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              const lr = s.last_run || {};
              setSyncSummary(lr);
              loadSyncLogs();
              loadUncategorized();
              loadAudit();
              loadHealth();
              const bs = lr.by_source || {};
              const type = lr.status === "error" ? "err" : "ok";
              setKeyStatus(
                "sync",
                type,
                `${lr.total_added || 0} videos synced in ${lr.execution_time_sec || 0}s (Movies ${bs.free_movies || 0} · New Channels ${bs.new_sources || 0} · Kids ${bs.kids || 0} · Music ${bs.music || 0} · Discovery ${bs.tmdb_discovery || 0}).`
              );
              if (type === "ok") {
                toast.success(`Auto-Sync Complete — ${lr.total_added || 0} New Titles Added`, {
                  description: `Movies ${bs.free_movies || 0} · New Channels ${bs.new_sources || 0} · Kids ${bs.kids || 0} · ${lr.execution_time_sec || 0}s`,
                });
              } else {
                toast.error("Auto-Sync finished with errors", { description: (lr.errors || []).slice(0, 2).join("; ") });
              }
              resolve();
            }
          } catch {
            /* gateway timeout / transient error — keep polling, never crash */
          }
        }, 5000);
      });
    } catch (e) {
      setKeyStatus("sync", "err", e.response?.data?.detail || "Sync failed. Check token / API keys.");
      toast.error("Auto-Sync failed", { description: e.response?.data?.detail || "Check token / API keys." });
    } finally {
      setBusy(null);
    }
  };

  const runYoutube = async () => {
    setBusy("yt");
    setKeyStatus("yt", null, "");
    try {
      const handles = ytHandles.split(",").map((h) => h.trim()).filter(Boolean);
      const { data } = await axios.post(
        `${API}/admin/import/youtube`,
        { handles, max_per_channel: Number(ytMax), genre: ytGenre },
        { headers }
      );
      setKeyStatus("yt", "ok", `Imported ${data.imported} videos as ${ytGenre}.`);
    } catch (e) {
      setKeyStatus("yt", "err", e.response?.data?.detail || "Import failed. Check token / API key.");
    } finally {
      setBusy(null);
    }
  };

  const runKidsSync = async () => {
    setBusy("kids");
    setKeyStatus("kids", null, "");
    try {
      const { data } = await axios.post(
        `${API}/admin/import/kids`,
        { max_per_channel: 8, tmdb_max_results: 12 },
        { headers }
      );
      const yt = data.by_source?.youtube ?? 0;
      const tm = data.by_source?.tmdb ?? 0;
      setKeyStatus("kids", "ok", `Kids Hub synced — ${yt} YouTube + ${tm} family movies.`);
      toast.success("Kids Hub synced", { description: `${data.imported} safe titles imported.` });
    } catch (e) {
      setKeyStatus("kids", "err", e.response?.data?.detail || "Kids sync failed. Check token / API key.");
    } finally {
      setBusy(null);
    }
  };

  const runRetagKids = async () => {
    setBusy("retag");
    setKeyStatus("kids", null, "");
    try {
      const { data } = await axios.post(`${API}/admin/retag-kids`, {}, { headers });
      setKeyStatus("kids", "ok", `Re-tagged ${data.retagged} items as Kids — homepage feed cleaned.`);
      toast.success("Cleanup complete", { description: `${data.retagged} items quarantined to Kids Hub.` });
    } catch (e) {
      setKeyStatus("kids", "err", e.response?.data?.detail || "Cleanup failed. Check token.");
    } finally {
      setBusy(null);
    }
  };

  const runYoutubeMovies = async () => {
    setBusy("ytm");
    setKeyStatus("ytm", null, "");
    try {
      const targets = ytmTargets
        .split("\n")
        .map((line) => {
          const [q, g] = line.split("|");
          return { query: (q || "").trim(), genre: (g || "").trim() };
        })
        .filter((t) => t.query);
      if (!targets.length) throw new Error("Add at least one 'query | genre' line.");
      const { data: started } = await axios.post(`${API}/admin/import/youtube-movies`, { targets, max_results: Number(ytmMax) }, { headers });
      setKeyStatus("ytm", "info", `Importing ${started.targets} source${started.targets === 1 ? "" : "s"} in the background — this can take a few minutes…`);
      let data = null;
      for (let i = 0; i < 180 && !data; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: st } = await axios.get(`${API}/admin/import/youtube-movies/status`, { headers });
        if (!st.running) data = st.result || { imported: 0, targets: started.targets, errors: ["Lost track of the import — check the catalog or run it again."] };
      }
      if (!data) throw new Error("Import is still running — check back shortly.");
      setKeyStatus("ytm", data.errors?.length ? "err" : "ok", `Imported ${data.imported} full-length titles across ${data.targets} sources.${data.errors?.length ? ` Issues: ${data.errors.join("; ")}` : ""}`);
    } catch (e) {
      setKeyStatus("ytm", "err", e.response?.data?.detail || e.message || "Import failed. Check token / API key.");
    } finally {
      setBusy(null);
    }
  };

  const runYoutubeMusic = async () => {
    setBusy("music");
    setKeyStatus("music", null, "");
    try {
      const targets = musicTargets
        .split("\n")
        .map((line) => {
          const [q, g] = line.split("|");
          return { query: (q || "").trim(), genre: (g || "").trim() || "Music" };
        })
        .filter((t) => t.query);
      const { data } = await axios.post(
        `${API}/admin/import/youtube-music`,
        { targets, max_results: Number(musicMax) },
        { headers }
      );
      setKeyStatus("music", "ok", `Imported ${data.imported} music videos across ${data.targets} sources.`);
    } catch (e) {
      setKeyStatus("music", "err", e.response?.data?.detail || "Import failed. Check token / API key.");
    } finally {
      setBusy(null);
    }
  };

  const runTmdb = async () => {
    setBusy("tmdb");
    setKeyStatus("tmdb", null, "");
    try {
      const { data } = await axios.post(
        `${API}/admin/import/tmdb-discovery`,
        { query: tmdbQuery.trim() },
        { headers }
      );
      setKeyStatus("tmdb", "ok", `Imported ${data.imported} discovery titles for "${data.query}".`);
    } catch (e) {
      setKeyStatus("tmdb", "err", e.response?.data?.detail || "Import failed. Check token / TMDb key.");
    } finally {
      setBusy(null);
    }
  };

  const runTmdbTrending = async (kind) => {
    setBusy(`tmdb-${kind}`);
    setKeyStatus("tmdb", null, "");
    try {
      const { data } = await axios.post(
        `${API}/admin/import/tmdb-trending`,
        { kind },
        { headers }
      );
      setKeyStatus("tmdb", "ok", `Imported ${data.imported} ${kind === "now_playing" ? "now-playing" : "trending"} titles.`);
    } catch (e) {
      setKeyStatus("tmdb", "err", e.response?.data?.detail || "Import failed. Check token / TMDb key.");
    } finally {
      setBusy(null);
    }
  };

  const runArchive = async () => {
    setBusy("ia");
    setKeyStatus("ia", null, "");
    try {
      const { data } = await axios.post(
        `${API}/admin/import/archive`,
        { collection: iaCollection.trim(), rows: Number(iaRows) },
        { headers }
      );
      setKeyStatus("ia", "ok", `Imported ${data.imported} public-domain films from the Internet Archive.`);
    } catch (e) {
      setKeyStatus("ia", "err", e.response?.data?.detail || "Import failed. Check token.");
    } finally {
      setBusy(null);
    }
  };

  const runCsv = async () => {
    if (!csvFile) return;
    setBusy("csv");
    setKeyStatus("csv", null, "");
    try {
      const form = new FormData();
      form.append("file", csvFile);
      const { data } = await axios.post(`${API}/admin/import-csv`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setKeyStatus("csv", "ok", `Imported ${data.imported} of ${data.rows_parsed} rows.`);
    } catch (e) {
      setKeyStatus("csv", "err", e.response?.data?.detail || "Upload failed. Check token / CSV format.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Admin · ReelVault</title>
      </Helmet>

      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-4 flex items-center gap-4">
          <Link to="/" data-testid="admin-home-link" className="flex items-center gap-2 group">
            <img src="/reelvault-mark.png" alt="ReelVault" data-testid="brand-logo" className="h-7 w-auto group-hover:scale-105 transition-transform duration-300" />
            <span className="font-serif text-xl tracking-tight text-[#EDEDED]">
              Reel<span className="text-crimson">Vault</span>
            </span>
          </Link>
          <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
            Admin Portal
          </span>
        </div>
      </header>

      {busy === "sync" && (
        <div
          data-testid="sync-progress-banner"
          className="sticky top-[57px] z-40 flex items-center justify-center gap-3 border-b border-amber/30 bg-amber/10 backdrop-blur-xl px-5 py-3 text-amber"
        >
          <CircleNotch size={18} className="animate-spin shrink-0" />
          <span className="font-mono text-xs uppercase tracking-[0.15em]">
            ⚡ Syncing catalog in the background… this can take a few minutes. You can keep using this page.
          </span>
        </div>
      )}

      <div className="mx-auto flex max-w-[1440px] flex-col lg:flex-row">
        <AdminSidebar tab={tab} setTab={setTab} token={token} saveToken={saveToken} />
        <main data-testid={`admin-view-${tab}`} className="rv-admin-main min-w-0 flex-1 px-5 sm:px-8 py-10 rv-fade">
          <AdminViewHeader tab={tab} />
          <div className="grid gap-6">
          {tab === "dashboard" && (
            <>
          <DashboardStats headers={headers} />
          {/* Revenue — affiliate click attribution */}
          <section
            data-testid="revenue-panel"
            className="border border-emerald-400/30 bg-gradient-to-br from-emerald-400/[0.06] to-transparent p-6 sm:p-8"
          >
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <CurrencyDollar size={26} weight="fill" className="text-emerald-300" />
                <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">Revenue · Affiliate Clicks</h2>
              </div>
              <button
                data-testid="revenue-refresh"
                onClick={loadRevenue}
                disabled={revenueLoading}
                className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60 hover:border-emerald-300 hover:text-emerald-300 transition-colors duration-300 disabled:opacity-40"
              >
                <ArrowClockwise size={14} weight="bold" className={revenueLoading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            {/* Totals + per-provider breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <div data-testid="revenue-total" className="border border-white/10 bg-[#0A0A0A] p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Total Clicks</p>
                <p className="mt-1 font-serif text-3xl text-emerald-300">{revenue?.total_clicks ?? 0}</p>
              </div>
              {(() => {
                const amazon = revenue?.by_provider?.find((p) => p.provider === "amazon")?.count ?? 0;
                const jw = revenue?.by_provider?.find((p) => p.provider === "justwatch")?.count ?? 0;
                return (
                  <>
                    <div data-testid="revenue-amazon" className="border border-white/10 bg-[#0A0A0A] p-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Amazon</p>
                      <p className="mt-1 font-serif text-3xl text-amber">{amazon}</p>
                    </div>
                    <div data-testid="revenue-justwatch" className="border border-white/10 bg-[#0A0A0A] p-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">JustWatch</p>
                      <p className="mt-1 font-serif text-3xl text-sky-300">{jw}</p>
                    </div>
                    <div data-testid="revenue-dispensary" className="border border-emerald-500/40 bg-emerald-500/[0.06] p-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300/70">🍁 Dispensary clicks</p>
                      <p data-testid="revenue-dispensary-total" className="mt-1 font-serif text-3xl text-emerald-300">{revenue?.dispensary?.total ?? 0}</p>
                      <p data-testid="revenue-dispensary-recent" className="mt-1 font-mono text-[10px] text-white/45">
                        24h: {revenue?.dispensary?.last_24h ?? 0} · 7d: {revenue?.dispensary?.last_7d ?? 0}
                      </p>
                      <p data-testid="revenue-dispensary-unique" className="mt-2 border-t border-emerald-500/20 pt-2 font-mono text-[10px] text-emerald-200/80">
                        Unique visitors: {revenue?.dispensary?.unique_total ?? 0} · 24h: {revenue?.dispensary?.unique_24h ?? 0} · 7d: {revenue?.dispensary?.unique_7d ?? 0}
                      </p>
                      <p className="mt-1 font-mono text-[9px] text-white/30">Your own clicks are excluded</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Other providers (if any beyond amazon/justwatch) */}
            {(revenue?.by_provider || []).filter((p) => !["amazon", "justwatch"].includes(p.provider)).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {revenue.by_provider
                  .filter((p) => !["amazon", "justwatch"].includes(p.provider))
                  .map((p) => (
                    <span key={p.provider} className="border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/60">
                      {p.provider}: <span className="text-emerald-300">{p.count}</span>
                    </span>
                  ))}
              </div>
            )}

            {revenue?.top_pages?.length > 0 && (
              <div data-testid="revenue-top-pages" className="mb-6">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Best-Converting Pages</h3>
                <div className="flex flex-col gap-1.5">
                  {revenue.top_pages.map((p) => (
                    <div key={p.page_url} className="flex items-center justify-between gap-4 border-b border-white/5 pb-1.5 font-mono text-xs">
                      <span className="truncate text-white/70">{p.page_url}</span>
                      <span className="text-emerald-300">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top click-driving titles */}
            <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Top Click-Driving Titles</h3>
            {revenue?.top_titles?.length ? (
              <div className="overflow-x-auto">
                <table data-testid="revenue-top-table" className="w-full text-left border-collapse">
                  <thead>
                    <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
                      <th className="py-2 pr-4 font-normal">#</th>
                      <th className="py-2 pr-4 font-normal">Title</th>
                      <th className="py-2 pr-4 font-normal text-right">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.top_titles.map((t, i) => (
                      <tr key={t.video_id || i} data-testid={`revenue-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-2.5 pr-4 font-mono text-xs text-white/40">{i + 1}</td>
                        <td className="py-2.5 pr-4 text-sm text-white/85 truncate max-w-[520px]">
                          {t.video_id ? (
                            <Link to={`/watch/${t.video_id}`} className="hover:text-amber transition-colors">{t.title || t.video_id}</Link>
                          ) : (t.title || "—")}
                        </td>
                        <td className="py-2.5 pr-4 text-right font-mono text-sm text-emerald-300">{t.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p data-testid="revenue-empty" className="font-mono text-[12px] text-white/35">
                No affiliate clicks recorded yet. Clicks on "Rent/Buy on Prime" and "Powered by JustWatch" will appear here.
              </p>
            )}
          </section>
          <ReferralPanel token={token} />
            </>
          )}
          {tab === "ingestion" && (
            <>
          <Fold testid="fold-full" title="Full-Length Movies (YouTube)" icon={<FilmSlate size={22} weight="duotone" className="text-crimson" />}>
          <Panel icon={<FilmSlate size={26} weight="duotone" className="text-crimson" />} title="Full-Length Movies (YouTube)">
            <p className="font-mono text-[11px] text-white/40 mb-4 leading-relaxed">
              One <span className="text-amber">query | genre</span> per line. <span className="text-amber">@handle</span> lines pull that channel's full-length uploads directly (60+ min; Zeus 40+); other lines search licensed distributors for movies over 40 min.
            </p>
            <div className="grid sm:grid-cols-[1fr_140px] gap-4">
              <div>
                <label className={label}>Distributor queries (query | genre)</label>
                <textarea
                  data-testid="ytm-targets-input"
                  value={ytmTargets}
                  onChange={(e) => setYtmTargets(e.target.value)}
                  rows={4}
                  className={`${field} resize-y`}
                  placeholder="FilmRise Movies full movie | Drama"
                />
              </div>
              <div>
                <label className={label}>Max / query</label>
                <input
                  data-testid="ytm-max-input"
                  type="number"
                  min="1"
                  max="50"
                  value={ytmMax}
                  onChange={(e) => setYtmMax(e.target.value)}
                  className={field}
                />
              </div>
            </div>
            <button data-testid="ytm-import-btn" onClick={runYoutubeMovies} disabled={busy === "ytm" || !authed} className={`${btn} mt-6`}>
              {busy === "ytm" ? <CircleNotch size={16} className="animate-spin" /> : <FilmSlate size={16} weight="bold" />}
              {busy === "ytm" ? "Importing…" : "Import Full Movies"}
            </button>
            <Status state={status.ytm} />
          </Panel>
          </Fold>
          <Fold testid="fold-playlist" title="YouTube Playlist Import" icon={<ListPlus size={22} weight="duotone" className="text-crimson" />}>
            <PlaylistImportPanel headers={headers} />
          </Fold>
          <Fold testid="fold-music" title="Music Videos (YouTube)" icon={<MusicNotes size={22} weight="duotone" className="text-amber" />}>
          <Panel icon={<MusicNotes size={26} weight="duotone" className="text-amber" />} title="Music Videos (YouTube)">
            <p className="font-mono text-[11px] text-white/40 mb-4 leading-relaxed">
              Pulls embeddable official music videos (YouTube Music category). One <span className="text-amber">query | genre</span> per line.
            </p>
            <div className="grid sm:grid-cols-[1fr_140px] gap-4">
              <div>
                <label className={label}>Music queries (query | genre)</label>
                <textarea
                  data-testid="music-targets-input"
                  value={musicTargets}
                  onChange={(e) => setMusicTargets(e.target.value)}
                  rows={3}
                  className={`${field} resize-y`}
                  placeholder="official music video VEVO | Music"
                />
              </div>
              <div>
                <label className={label}>Max / query</label>
                <input
                  data-testid="music-max-input"
                  type="number"
                  min="1"
                  max="50"
                  value={musicMax}
                  onChange={(e) => setMusicMax(e.target.value)}
                  className={field}
                />
              </div>
            </div>
            <button data-testid="music-import-btn" onClick={runYoutubeMusic} disabled={busy === "music" || !authed} className={`${btn} mt-6`}>
              {busy === "music" ? <CircleNotch size={16} className="animate-spin" /> : <MusicNotes size={16} weight="bold" />}
              {busy === "music" ? "Importing…" : "Import Music Videos"}
            </button>
            <Status state={status.music} />
          </Panel>
          </Fold>
          <Fold testid="fold-kids" title="Kids Hub Instant Sync" icon={<Baby size={22} weight="duotone" className="text-sky-400" />}>
          <section
            data-testid="kids-sync-panel"
            className="border border-sky-400/30 bg-gradient-to-br from-sky-400/[0.07] to-transparent p-6 sm:p-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <Baby size={26} weight="duotone" className="text-sky-400" />
              <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">Kids Hub Instant Sync</h2>
            </div>
            <p className="font-mono text-[11px] text-white/40 mb-6 leading-relaxed">
              One-click import of vetted under-5 channels (<span className="text-sky-400">@Numberblocks, @msrachel, @CoComelon, @SuperSimpleSongs</span>)
              plus TMDb G-rated family movies. Everything is auto-flagged <span className="text-sky-400">kids</span> and quarantined off the main feed.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="kids-sync-btn"
                onClick={runKidsSync}
                disabled={busy === "kids" || !authed}
                className="flex items-center justify-center gap-2 border border-sky-400 bg-sky-400 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-black font-bold hover:bg-sky-300 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === "kids" ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
                {busy === "kids" ? "Syncing Kids Hub…" : "Sync Kids Hub Now"}
              </button>
              <button
                data-testid="kids-retag-btn"
                onClick={runRetagKids}
                disabled={busy === "retag" || !authed}
                className="flex items-center justify-center gap-2 border border-sky-400/40 bg-transparent px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-sky-400 hover:bg-sky-400/10 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Re-file any existing kids-channel videos as Kids and clean the homepage feed"
              >
                {busy === "retag" ? <CircleNotch size={16} className="animate-spin" /> : <Broom size={16} weight="duotone" />}
                {busy === "retag" ? "Cleaning…" : "Re-Tag / Cleanup"}
              </button>
            </div>
            <Status state={status.kids} />
          </section>
          </Fold>
          <Fold testid="fold-hollywood" title="Hollywood Discovery (TMDb)" icon={<FilmSlate size={22} weight="duotone" className="text-amber" />}>
          <Panel icon={<FilmSlate size={26} weight="duotone" className="text-crimson" />} title="Hollywood Discovery (TMDb)">
            <p className="font-mono text-[11px] text-white/40 mb-4 leading-relaxed">
              Pulls major movies as <span className="text-crimson">discovery titles</span> — official trailer + "Stream / Rent on" links (JustWatch). Never streams full films.
            </p>
            <label className={label}>Movie search</label>
            <input
              data-testid="tmdb-query-input"
              value={tmdbQuery}
              onChange={(e) => setTmdbQuery(e.target.value)}
              className={field}
              placeholder="Bad Boys, Transformers, Dune…"
            />
            <button data-testid="tmdb-import-btn" onClick={runTmdb} disabled={busy === "tmdb" || !authed || !tmdbQuery.trim()} className={`${btn} mt-6`}>
              {busy === "tmdb" ? <CircleNotch size={16} className="animate-spin" /> : <FilmSlate size={16} weight="bold" />}
              {busy === "tmdb" ? "Importing…" : "Import Discovery Titles"}
            </button>

            <div className="mt-4 h-px w-full bg-white/10" />
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">Fresh Releases</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="tmdb-trending-btn" onClick={() => runTmdbTrending("week")} disabled={busy === "tmdb-week" || !authed} className={btn}>
                {busy === "tmdb-week" ? <CircleNotch size={16} className="animate-spin" /> : <FilmSlate size={16} weight="bold" />}
                {busy === "tmdb-week" ? "Importing…" : "Trending This Week"}
              </button>
              <button data-testid="tmdb-nowplaying-btn" onClick={() => runTmdbTrending("now_playing")} disabled={busy === "tmdb-now_playing" || !authed} className={btn}>
                {busy === "tmdb-now_playing" ? <CircleNotch size={16} className="animate-spin" /> : <FilmSlate size={16} weight="bold" />}
                {busy === "tmdb-now_playing" ? "Importing…" : "Now Playing"}
              </button>
            </div>
            <Status state={status.tmdb} />
          </Panel>
          </Fold>
          <Fold testid="fold-archive" title="Internet Archive" icon={<Archive size={22} weight="duotone" className="text-amber" />}>
          <Panel icon={<Archive size={26} weight="duotone" className="text-amber" />} title="Internet Archive">
            <div className="grid sm:grid-cols-[1fr_140px] gap-4">
              <div>
                <label className={label}>Collection</label>
                <input
                  data-testid="ia-collection-input"
                  value={iaCollection}
                  onChange={(e) => setIaCollection(e.target.value)}
                  className={field}
                  placeholder="prelinger"
                />
              </div>
              <div>
                <label className={label}>Rows</label>
                <input
                  data-testid="ia-rows-input"
                  type="number"
                  min="1"
                  max="100"
                  value={iaRows}
                  onChange={(e) => setIaRows(e.target.value)}
                  className={field}
                />
              </div>
            </div>
            <button data-testid="ia-import-btn" onClick={runArchive} disabled={busy === "ia" || !authed} className={`${btn} mt-6`}>
              {busy === "ia" ? <CircleNotch size={16} className="animate-spin" /> : <Archive size={16} weight="bold" />}
              {busy === "ia" ? "Importing…" : "Import from Archive"}
            </button>
            <Status state={status.ia} />
          </Panel>
          </Fold>
          <Fold testid="fold-csv" title="CSV Bulk Import" icon={<UploadSimple size={22} weight="duotone" className="text-amber" />}>
          <Panel icon={<UploadSimple size={26} weight="duotone" className="text-amber" />} title="CSV Bulk Import">
            <p className="font-mono text-[11px] text-white/40 mb-4 leading-relaxed">
              Columns: <span className="text-amber">video_id, title, duration_sec, views, keywords, thumbnails, default_thumb, monetized_embed_url</span>.
              Separate multiple keywords/thumbnails with a <span className="text-amber">|</span> pipe.
            </p>
            <input
              data-testid="csv-file-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white/70 font-mono file:mr-4 file:border file:border-white/20 file:bg-[#111] file:px-4 file:py-2 file:text-amber file:font-mono file:text-xs file:uppercase file:tracking-wider hover:file:border-amber file:cursor-pointer"
            />
            <button data-testid="csv-import-btn" onClick={runCsv} disabled={busy === "csv" || !authed || !csvFile} className={`${btn} mt-6`}>
              {busy === "csv" ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />}
              {busy === "csv" ? "Uploading…" : "Upload & Import"}
            </button>
            <Status state={status.csv} />
          </Panel>
          </Fold>
          <Fold testid="fold-addlink" title="Add by YouTube Link" icon={<LinkSimple size={22} weight="duotone" className="text-amber" />}>
          <Panel icon={<LinkSimple size={26} weight="duotone" className="text-amber" />} title="Add by YouTube Link">
            <p className="text-white/40 text-sm mb-4">
              Paste any YouTube link to instantly add a favourite. It's auto-checked for
              embeddability and US playback — region-locked or promo/teaser clips are rejected
              with a reason so nothing broken reaches your rows.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="add-link-url"
                className={field + " flex-1"}
                placeholder="https://youtu.be/… or https://www.youtube.com/watch?v=…"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !linkBusy && addByLink()}
              />
              <input
                data-testid="add-link-tags"
                className={field + " sm:w-56"}
                placeholder="tags (optional, e.g. anime)"
                value={linkTags}
                onChange={(e) => setLinkTags(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !linkBusy && addByLink()}
              />
              <button
                data-testid="add-link-btn"
                className={btn + " shrink-0"}
                onClick={addByLink}
                disabled={linkBusy}
              >
                {linkBusy ? <CircleNotch size={16} className="animate-spin" /> : <LinkSimple size={16} weight="bold" />}
                {linkBusy ? "Checking…" : "Add Video"}
              </button>
            </div>
          </Panel>
          </Fold>
          <ImportHistory headers={headers} />
            </>
          )}
          {tab === "queue" && (
            <>
          <TitleOverridePanel headers={headers} />
          <Panel icon={<ClockCounterClockwise size={26} weight="duotone" className="text-amber" />} title="Auto-Tag Audit">
            <p className="text-white/40 text-sm mb-4">
              The most recently auto-tagged videos, newest first, showing which waterfall tier
              classified each one: <TierBadge source="tmdb" /> real genre from TMDb ·{" "}
              <TierBadge source="channel" /> inherited from the source channel ·{" "}
              <TierBadge source="keyword" /> matched a title/description keyword ·{" "}
              <TierBadge source="uncategorized" /> parked for review.
            </p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="audit-counts">
              {["tmdb", "channel", "keyword", "uncategorized"].map((t) => (
                <div key={t} className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-3 py-1.5">
                  <TierBadge source={t} />
                  <span className="font-mono text-sm text-white/70">{auditCounts[t] ?? 0}</span>
                </div>
              ))}
            </div>
            {auditItems.length === 0 ? (
              <p data-testid="audit-empty" className="font-mono text-[12px] text-white/30">
                No auto-tagged videos yet — run a sync to populate this.
              </p>
            ) : (
              <div data-testid="audit-list" className="space-y-2 max-h-[480px] overflow-auto pr-1">
                {auditItems.map((v) => (
                  <div
                    key={v.video_id}
                    data-testid={`audit-row-${v.video_id}`}
                    className="flex flex-wrap items-center gap-3 border border-white/10 bg-[#0A0A0A] p-2.5"
                  >
                    {v.default_thumb ? (
                      <img src={v.default_thumb} alt="" className="h-10 w-16 object-cover rounded shrink-0" />
                    ) : (
                      <div className="h-10 w-16 bg-white/5 rounded shrink-0" />
                    )}
                    <div className="flex-1 min-w-[160px]">
                      <div className="text-white/80 text-sm leading-snug truncate">{v.title}</div>
                      <div className="font-mono text-[10px] text-white/30 truncate">
                        {v.release_year || "—"} · {(v.keywords || []).join(", ") || "no tags"}
                      </div>
                    </div>
                    <TierBadge source={v.tag_source} />
                    {(v.tag_source === "keyword" || v.tag_source === "uncategorized") && (
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <input
                          data-testid={`audit-retag-input-${v.video_id}`}
                          className={field + " w-40"}
                          placeholder="override genres"
                          value={retagInputs[v.video_id] || ""}
                          onChange={(e) => setRetagInputs((t) => ({ ...t, [v.video_id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && retagVideo(v.video_id)}
                        />
                        <button
                          data-testid={`audit-retag-btn-${v.video_id}`}
                          className="shrink-0 border border-violet-400/50 text-violet-300 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] hover:bg-violet-400/10 transition-colors"
                          onClick={() => retagVideo(v.video_id)}
                        >
                          Re-tag
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <section
            data-testid="sync-all-panel"
            className="border border-amber/30 bg-gradient-to-br from-amber/[0.07] to-transparent p-6 sm:p-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <Lightning size={26} weight="fill" className="text-amber" />
              <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">Auto-Sync All Genres</h2>
            </div>
            <p className="font-mono text-[11px] text-white/40 mb-6 leading-relaxed">
              One click runs a full-spectrum pull — genre trailers (TMDb), full-length indie films &amp; top free-movie
              channels (YouTube), public-domain titles (Archive), the Kids Hub, music visualizers, live streams and
              TMDb trending. Runs automatically every night at <span className="text-amber">02:00 UTC</span>.
              Kids channels are auto-quarantined. Batches stay small (~6-10/source) to protect API quota.
            </p>

            <div className="flex flex-wrap items-center gap-5 mb-6">
              {[
                { key: "tmdb", label: "Genre TMDb", val: syncTmdb, set: setSyncTmdb, testid: "sync-toggle-tmdb" },
                { key: "archive", label: "Archive", val: syncArchive, set: setSyncArchive, testid: "sync-toggle-archive" },
                { key: "youtube", label: "Genre YouTube", val: syncYt, set: setSyncYt, testid: "sync-toggle-youtube" },
                { key: "free_movies", label: "🎬 Free Movie Channels", val: syncFreeMovies, set: setSyncFreeMovies, testid: "sync-toggle-free-movies" },
                { key: "kids", label: "👶 Kids Hub", val: syncKids, set: setSyncKids, testid: "sync-toggle-kids" },
                { key: "discovery", label: "🍿 TMDb Discovery", val: syncDiscovery, set: setSyncDiscovery, testid: "sync-toggle-discovery" },
                { key: "music", label: "🎵 Music & Visualizers", val: syncMusic, set: setSyncMusic, testid: "sync-toggle-music" },
                { key: "new_sources", label: "📺 New Channels (6)", val: syncNewSources, set: setSyncNewSources, testid: "sync-toggle-new-sources" },
              ].map((t) => (
                <label key={t.key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    data-testid={t.testid}
                    type="checkbox"
                    checked={t.val}
                    onChange={(e) => t.set(e.target.checked)}
                    className="h-4 w-4 accent-amber"
                  />
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/70">{t.label}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">YT / genre</span>
                <input
                  data-testid="sync-yt-max-input"
                  type="number"
                  min="1"
                  max="10"
                  value={ytMaxSync}
                  onChange={(e) => setYtMaxSync(e.target.value)}
                  disabled={!syncYt}
                  className="w-16 bg-[#111] border border-white/10 px-2 py-1.5 text-white text-center focus:outline-none focus:border-amber font-mono text-sm disabled:opacity-40"
                />
              </div>
            </div>

            <button
              data-testid="sync-all-btn"
              onClick={runSyncAll}
              disabled={busy === "sync" || !authed}
              className={`${btn} !border-amber !bg-amber !text-black hover:!bg-amber/80`}
            >
              {busy === "sync" ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
              {busy === "sync" ? "Running full-spectrum sync…" : "Run Full Site Auto-Sync Now"}
            </button>
            <Status state={status.sync} />

            {syncSummary && (
              <div data-testid="sync-summary" className="mt-6 border border-white/10 bg-[#0A0A0A] p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Execution Breakdown</span>
                  <span className="font-mono text-xs text-amber">
                    +{syncSummary.total_added} · {syncSummary.execution_time_sec}s
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-xs">
                    <thead>
                      <tr className="text-white/40 text-left">
                        <th className="pb-2 pr-4 font-normal">Genre</th>
                        <th className="pb-2 pr-4 font-normal text-right">TMDb</th>
                        <th className="pb-2 pr-4 font-normal text-right">Archive</th>
                        <th className="pb-2 pr-4 font-normal text-right">YouTube</th>
                        <th className="pb-2 font-normal text-right text-amber">Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(syncSummary.by_genre || syncSummary.by_source || {}).map(([g, s]) => (
                        <tr key={g} className="border-t border-white/5 text-white/70">
                          <td className="py-1.5 pr-4">{g}</td>
                          <td className="py-1.5 pr-4 text-right">{typeof s === "object" ? s?.tmdb ?? 0 : "—"}</td>
                          <td className="py-1.5 pr-4 text-right">{typeof s === "object" ? s?.archive ?? 0 : "—"}</td>
                          <td className="py-1.5 pr-4 text-right">{typeof s === "object" ? s?.youtube ?? 0 : "—"}</td>
                          <td className="py-1.5 text-right text-amber">{typeof s === "object" ? s?.added ?? 0 : s}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {syncSummary.errors?.length > 0 && (
                  <div className="mt-4 border border-crimson/40 p-3 text-crimson font-mono text-[11px]">
                    {syncSummary.errors.length} source error(s): {syncSummary.errors.slice(0, 3).join("; ")}
                  </div>
                )}
              </div>
            )}

            {syncLogs.length > 0 && (
              <div data-testid="sync-log" className="mt-6 border border-white/10 bg-[#0A0A0A] p-5">
                <div className="flex items-center gap-2 mb-4">
                  <ClockCounterClockwise size={16} weight="duotone" className="text-amber" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Recent Sync Runs</span>
                </div>
                <div className="space-y-2">
                  {syncLogs.map((r, i) => {
                    const d = r.created_at ? new Date(r.created_at) : null;
                    const when = d ? d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                    const ok = r.status === "success";
                    return (
                      <div key={i} data-testid="sync-log-row" className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2 font-mono text-[11px]">
                        <div className="flex items-center gap-3">
                          <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-400" : r.status === "partial" ? "bg-amber" : "bg-crimson"}`} />
                          <span className="text-white/70">{when}</span>
                          <span className="text-white/30 uppercase tracking-[0.12em]">{r.trigger}</span>
                        </div>
                        <div className="flex items-center gap-3 text-white/50">
                          <span className="text-amber">+{r.total_added}</span>
                          <span>{r.execution_time_sec}s</span>
                          {r.error_count > 0 && <span className="text-crimson">{r.error_count} err</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <Panel icon={<FilmSlate size={26} weight="duotone" className="text-amber" />} title={`Needs Review (${uncatTotal})`}>
            <p className="text-white/40 text-sm mb-4">
              Films whose TMDb genre lookup failed are parked here and stay hidden from the public rows.
              Assign comma-separated genre tags to publish, or remove them.
            </p>
            {uncatItems.length === 0 ? (
              <p data-testid="needs-review-empty" className="font-mono text-[12px] text-white/30">Nothing waiting for review. 🎉</p>
            ) : (
              <div data-testid="needs-review-list" className="space-y-3 max-h-[480px] overflow-auto pr-1">
                {uncatItems.map((v) => (
                  <div key={v.video_id} data-testid={`needs-review-row-${v.video_id}`} className="flex flex-wrap items-center gap-3 border border-white/10 bg-[#0A0A0A] p-3">
                    {v.default_thumb ? (
                      <img src={v.default_thumb} alt="" className="h-12 w-20 object-cover rounded" />
                    ) : (
                      <div className="h-12 w-20 bg-white/5 rounded" />
                    )}
                    <div className="flex-1 min-w-[160px]">
                      <div className="text-white/80 text-sm leading-snug line-clamp-2">{v.title}</div>
                      <div className="font-mono text-[10px] text-white/30">{v.release_year || "—"} · {Math.round((v.duration_sec || 0) / 60)}min</div>
                    </div>
                    <input
                      data-testid={`needs-review-tags-${v.video_id}`}
                      className={field + " w-40"}
                      placeholder="action, thriller"
                      value={uncatTags[v.video_id] || ""}
                      onChange={(e) => setUncatTags((t) => ({ ...t, [v.video_id]: e.target.value }))}
                    />
                    <button
                      data-testid={`needs-review-approve-${v.video_id}`}
                      className={btn}
                      onClick={() => approveUncat(v.video_id)}
                    >
                      Approve
                    </button>
                    <button
                      data-testid={`needs-review-reject-${v.video_id}`}
                      className="border border-crimson/40 text-crimson px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-crimson/10 transition-colors"
                      onClick={() => rejectUncat(v.video_id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <BrokenLinksPanel headers={headers} />
          <DuplicatesPanel headers={headers} />
            </>
          )}
          {tab === "channels" && (
            <>
          <Panel icon={<Pulse size={26} weight="duotone" className="text-emerald-400" />} title="Channel Health">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-white/40 text-sm max-w-2xl">
                Monitors every source channel for content drift — flags channels posting off-topic
                titles (gaming, crypto, vlogs, reactions) that signal a channel was sold, hacked, or
                pivoted, before the pipeline dumps them onto the homepage.
              </p>
              <button
                data-testid="health-refresh-btn"
                className={btn + " shrink-0"}
                onClick={loadHealth}
                disabled={healthLoading}
              >
                <ArrowClockwise size={16} className={healthLoading ? "animate-spin" : ""} />
                Re-scan
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="health-summary">
              {["offtopic", "warning", "healthy"].map((s) => (
                <div key={s} className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-3 py-1.5">
                  <HealthBadge status={s} />
                  <span className="font-mono text-sm text-white/70">{healthSummary[s] ?? 0}</span>
                </div>
              ))}
            </div>
            {healthChannels.length === 0 ? (
              <p data-testid="health-empty" className="font-mono text-[12px] text-white/30">
                No channel data yet — run a sync to populate source-channel tracking.
              </p>
            ) : (
              <div data-testid="health-list" className="space-y-2 max-h-[480px] overflow-auto pr-1">
                {healthChannels.map((c) => (
                  <div
                    key={c.channel}
                    data-testid={`health-row-${c.status}`}
                    className={`flex flex-wrap items-center gap-3 border bg-[#0A0A0A] p-3 ${
                      c.quarantined ? "border-crimson/60 opacity-70" : c.status === "offtopic" ? "border-crimson/40" : "border-white/10"
                    }`}
                  >
                    <HealthBadge status={c.status} />
                    {c.quarantined && (
                      <span className="inline-flex items-center shrink-0 border border-crimson/60 bg-crimson/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-crimson">
                        Quarantined
                      </span>
                    )}
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-white/85 text-sm leading-snug truncate">{c.channel}</div>
                      <div className="font-mono text-[10px] text-white/30 truncate">
                        {c.count} videos · tags: {(c.top_tags || []).join(", ") || "—"}
                        {c.flagged_count > 0 && (
                          <span className="text-crimson"> · {c.flagged_count} flagged ({Math.round(c.flagged_ratio * 100)}%)</span>
                        )}
                      </div>
                      {c.sample_flagged && c.sample_flagged.length > 0 && (
                        <div className="font-mono text-[10px] text-crimson/70 truncate mt-0.5">
                          ⚠ {c.sample_flagged.join(" · ")}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`quarantine-btn-${c.channel}`}
                      onClick={() => quarantineChannel(c.channel, c.quarantined)}
                      className={`shrink-0 inline-flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                        c.quarantined
                          ? "border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10"
                          : "border-crimson/50 text-crimson hover:bg-crimson/10"
                      }`}
                    >
                      <Prohibit size={14} weight="bold" />
                      {c.quarantined ? "Release" : "Quarantine"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <Panel icon={<YoutubeLogo size={26} weight="duotone" className="text-crimson" />} title="YouTube Channels">
            <div className="grid sm:grid-cols-[1fr_140px] gap-4">
              <div>
                <label className={label}>Channel handles (comma-separated)</label>
                <input
                  data-testid="yt-handles-input"
                  value={ytHandles}
                  onChange={(e) => setYtHandles(e.target.value)}
                  className={field}
                  placeholder="@shudder"
                />
              </div>
              <div>
                <label className={label}>Max / channel</label>
                <input
                  data-testid="yt-max-input"
                  type="number"
                  min="1"
                  max="50"
                  value={ytMax}
                  onChange={(e) => setYtMax(e.target.value)}
                  className={field}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className={label}>Target category</label>
              <select
                data-testid="yt-genre-select"
                value={ytGenre}
                onChange={(e) => setYtGenre(e.target.value)}
                className={field}
              >
                <option value="Indie">Indie Films</option>
                <option value="Music">Music &amp; Visualizers</option>
                <option value="Podcasts">Podcasts &amp; Interviews</option>
                <option value="Kids">Kids Hub (Under-5)</option>
              </select>
              <p className="mt-2 font-mono text-[10px] text-white/30">
                Vetted kids channels (Numberblocks, Ms Rachel, Cocomelon, Super Simple) are auto-flagged as Kids regardless of this setting.
              </p>
            </div>
            <button data-testid="yt-import-btn" onClick={runYoutube} disabled={busy === "yt" || !authed} className={`${btn} mt-6`}>
              {busy === "yt" ? <CircleNotch size={16} className="animate-spin" /> : <YoutubeLogo size={16} weight="bold" />}
              {busy === "yt" ? "Importing…" : "Import from YouTube"}
            </button>
            <Status state={status.yt} />
          </Panel>
            </>
          )}
          </div>
        </main>
      </div>
    </div>
  );
}
